import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Navbar, Nav, Button, Container } from 'react-bootstrap';
import { Link, useLocation, useHistory } from 'react-router-dom';
import './App.scss';
import { useDispatch, useSelector } from 'react-redux';
import { dataActions, AppState, useUserParam, useIsFetchingUser, useUser } from 'reducers';
import Login from '../Login';
import { getFormattedDateWithHoursAndMinutes, USER_ROLE_SUPERVISIOR } from 'lib/util';
import { setSliceState } from 'reducers/conf';
import store from 'store';
import { DetailedOrder } from 'lib/types';
import { API_ADDRESS, buildResourceLink, get, logout } from 'lib/communication';
import Spinner from 'components/Spinner';
import { SpinnerButtonType } from 'components/Spinner/Spinner';
import ButtonComponent from 'components/ButtonComponent';
import NotificationComponent from 'components/NotificationComponent';

import RequestErrorModal from 'components/RequestErrorModal/RequestErrorModal';
import { authUser, getCurrentOrIncomingServiceWorks } from 'lib/communication/userInfo';
import permissionService, { DEF_WINDOW, sessionService } from 'services';
import { getAdditionalTabName } from 'lib/communication/additionalTab';
import TagManager from 'react-gtm-module';
import { GA_identifyUser, GA_pageViewed } from 'lib/gtm-utils';
import UrlExpired from 'containers/UrlExpired';
import { generateTempLink } from 'lib/communication/files';
import { USER_LOCAL_STORAGE } from 'services/session.service';
import NavigationFactoryItems from 'components/NavigationFactory/NavigationFactoryItems';
import { NAVBAR_TABS } from './navConfig';
import { hasNavPermission, normalizePath, resolveActiveNav } from 'lib/navigation';
import AppRoutes from './AppRoutes';
import { useDevice } from 'lib/hooks/useDevice';

(window as any).clearCache = () => {
    get(`${API_ADDRESS}clearCache`)
        .then(
            () => console.log('Cache cleared'),
            (error) => console.log(error)
        );
};

const App = () => {
    const history = useHistory();
    const [expanded, setExpanded] = useState(false);
    const logoutClick = async () => {
        setExpanded(false);
        logout();
        history.push(process.env.PUBLIC_URL);
    };
    if (sessionStorage.getItem('logout') === 'true') {
        logoutClick();
    }
    const dispatch = useDispatch();
    const location = useLocation();
    const user = useUser();
    const page = useUserParam(DEF_WINDOW);
    const isUserFetching = useIsFetchingUser();
    const { isPhone, isDesktop } = useDevice();
    const [isISODVisible, setVisibility] = useState('none');
    const brandColor = useSelector((state: AppState) => state.conf.brandColor);
    const brandLightColor = useSelector((state: AppState) => state.conf.brandLightColor);
    const brandBrightColor = useSelector((state: AppState) => state.conf.brandBrightColor);
    const tableFontColor = useSelector((state: AppState) => state.conf.tableFontColor);
    const [activeKey, setActiveKey] = useState('news');
    const [activeKeyName, setActiveKeyName] = useState('Aktualności');
    const tooManyAttempts = useSelector((state: AppState) => state.requests.tooManyAttempts);
    const invalidToken = useSelector((state: AppState) => state.requests.invalidToken);
    const edit = useSelector((state: AppState) => state.data.orders.edit);
    const append = useSelector((state: AppState) => state.data.orders.append);
    const logo = useSelector((state: AppState) => state.conf.logo);
    const company = useSelector((state: AppState) => state.conf.companyId);

    const shutdownHours = useSelector((state: AppState) => state.data.userInfo.shutdownHours);
    const serviceWorks = useSelector((state: AppState) => state.data.adminInfo.serviceWorks);
    const currentOrIncomigServiceWorks = useSelector((state: AppState) => state.data.userInfo.currentOrIncomigServiceWorks);
    const [isDisabled, setIsDisabled] = useState(false);
    const [disableTime, setDisableTime] = useState('');
    const [disableMessage, setDisableMessage] = useState('');
    const gtmId = useSelector((state: AppState) => state.conf.gtmId);

    const additionalTabInfo = useSelector((state: AppState) => state.data.additionalTab.details);

    const isConfigIncorrect = useSelector((state: AppState) => state.requests.incorrectConfig);

    const userJustLoggedIn = useSelector((state: AppState) => state.data.userInfo.justLoggedIn);

    const zamowieniaServiceAddress = useSelector((state: AppState) => state.conf.zamowieniaServiceAddress);

    const [cartReset, setCartReset] = useState(sessionStorage.getItem('cartReset') === 'true');

    const [showUsersDropdown, setShowUsersDropdown] = useState(false);
    const [showTermsDropdown, setShowTermsDropdown] = useState(false);

    const queryParams = new URLSearchParams(location.search);
    const fileNameToGenerateForPreviewOnUrlExpired = queryParams.get('fileName');

    useEffect(() => {
        if (user !== null || !zamowieniaServiceAddress) return;
        dispatch(authUser);
    }, [dispatch, zamowieniaServiceAddress]);

    useEffect(() => {
        if (cartReset) {
            sessionStorage.setItem('cartReset', 'true');
        } else {
            sessionStorage.setItem('cartReset', 'false');
        }
    }, [cartReset]);

    useEffect(() => {
        if (localStorage.getItem('gutter_promotion_day_show') == null || localStorage.getItem('gutter_promotion_day_show') !== new Date().toISOString().slice(0, 10).replace(/-/g, '')) localStorage.setItem('gutter_promotion_showed', 'false');
    }, []);

    useEffect(() => {
        if (gtmId) {
            TagManager.initialize({ gtmId });
        }
    }, [gtmId]);

    useEffect(() => {
        GA_identifyUser(user === null ? null : {
            role: user.role,
            companyId: user.company
        });
    }, [user]);

    const pageTitle = useMemo(() => {
        const publicUrl = process.env.PUBLIC_URL || '';
        const normalized = normalizePath(location.pathname);
        const base = normalizePath(publicUrl);
        if (normalized.startsWith(`${base}/changePassword`)) return 'Zmiana hasła';
        if (normalized.startsWith(`${base}/cookie-policy`)) return 'Polityka cookies';
        const navContext = { user, isPhone, additionalTabName: additionalTabInfo.name };
        return resolveActiveNav(NAVBAR_TABS, navContext, location.pathname, publicUrl).activeLabel ?? 'Zamówienia online';
    }, [location.pathname, user, isPhone, additionalTabInfo.name]);

    const lastPageView = useRef<string | null>(null);

    useEffect(() => {
        if (user === null) {
            lastPageView.current = null;
            return;
        }
        const path = normalizePath(location.pathname).replace(/\/\d+$/, '');
        if (lastPageView.current === path) return;
        lastPageView.current = path;
        GA_pageViewed(path, pageTitle);
    }, [location.pathname, pageTitle, user]);

    useEffect(() => {
        if (zamowieniaServiceAddress.length > 0) {
            getCurrentOrIncomingServiceWorks();
            getAdditionalTabName();
        }
    }, [zamowieniaServiceAddress]);

    useEffect(() => {
        if (company === null || user === null) {
            return;
        }

        const publicUrl = process.env.PUBLIC_URL || '';
        const currentPath = location.pathname;
        const normalizedCurrentPath = normalizePath(currentPath);
        const normalizedPublicUrl = normalizePath(publicUrl);
        const navContext = { user, isPhone, additionalTabName: additionalTabInfo.name };

        const getDefaultTab = () => {
            if (hasNavPermission('news', user)) {
                return { id: 'news', label: 'Aktualności', path: '/news' };
            }
            if (hasNavPermission('orders', user)) {
                return { id: 'orders', label: 'Zamówienia', path: '/orders' };
            }

            return { id: 'promotions', label: 'Wspólne korzyści', path: '/promotions' };
        };

        const isRoot = normalizedCurrentPath === normalizedPublicUrl;
        const isNewsPath = normalizedCurrentPath.startsWith(`${normalizedPublicUrl}/news`) && !normalizedCurrentPath.startsWith(`${normalizedPublicUrl}/admin/news`);

        if (isRoot || isNewsPath) {
            const defaultTab = getDefaultTab();
            setActiveKey(defaultTab.id);
            setActiveKeyName(defaultTab.label);

            const targetPath = normalizePath(`${publicUrl}${defaultTab.path}`);
            if (isRoot || (isNewsPath && !hasNavPermission('news', user))) {
                if (normalizedCurrentPath !== targetPath) {
                    history.push(`${publicUrl}${defaultTab.path}`);
                }
            }

            return;
        }

        if (normalizedCurrentPath.startsWith(`${normalizedPublicUrl}/changePassword`)) {
            setActiveKey('change-password');
            setActiveKeyName('Zmiana hasła');
            return;
        }

        if (normalizedCurrentPath.startsWith(`${normalizedPublicUrl}/cookie-policy`)) {
            setActiveKey('cookie-policy');
            setActiveKeyName('Polityka cookies');
            return;
        }

        const resolvedNav = resolveActiveNav(NAVBAR_TABS, navContext, currentPath, publicUrl);
        if (resolvedNav.hasVisibleMatch && resolvedNav.activeKey && resolvedNav.activeLabel) {
            setActiveKey(resolvedNav.activeKey);
            setActiveKeyName(resolvedNav.activeLabel);
            return;
        }

        if (resolvedNav.hasAnyMatch) {
            history.push(publicUrl);
            return;
        }

        if (user !== null && !normalizedCurrentPath.startsWith(`${normalizedPublicUrl}/expired-url`)) {
            const defaultTab = getDefaultTab();
            setActiveKey(defaultTab.id);
            setActiveKeyName(defaultTab.label);

            const targetPath = normalizePath(`${publicUrl}${defaultTab.path}`);
            if (normalizedCurrentPath !== targetPath) {
                history.push(`${publicUrl}${defaultTab.path}`);
            }
        }

        const currentDate = new Date();
        if (user?.permissions.canManageServiceWorks) {
            setDisableTime('');
            setDisableMessage('');
            setIsDisabled(false);
        } else if (currentDate > currentOrIncomigServiceWorks.from) {
            setDisableTime(`od ${getFormattedDateWithHoursAndMinutes(currentOrIncomigServiceWorks.from)} do ${getFormattedDateWithHoursAndMinutes(currentOrIncomigServiceWorks.to)}`);
            setDisableMessage(currentOrIncomigServiceWorks.message);
            setIsDisabled(true);
        } else if (shutdownHours.from && shutdownHours.to && shutdownHours.from < currentDate.getHours() && shutdownHours.to > currentDate.getHours()) {
            setDisableTime(`w godzinach ${shutdownHours.from}:00 - ${shutdownHours.to}:00`);
            setIsDisabled(true);
        }
    }, [location.pathname, history, company, user, shutdownHours, serviceWorks, currentOrIncomigServiceWorks.from, currentOrIncomigServiceWorks.message, additionalTabInfo.name, isPhone]);

    useEffect(() => {
        document.documentElement.style.setProperty('--brand', brandColor);
        document.documentElement.style.setProperty('--brand-light', brandLightColor);
        document.documentElement.style.setProperty('--brand-bright', brandBrightColor);
        document.documentElement.style.setProperty('--table-font-color', tableFontColor);
    }, [brandColor, brandLightColor, brandBrightColor, tableFontColor]);

    const changeViewToDefault = () => {
        switch (page) {
            case 'Akt':
                history.replace(`${process.env.PUBLIC_URL}/news`);
                break;
            case 'Zam':
                history.replace(`${process.env.PUBLIC_URL}/orders`);
                break;
            case 'NZam':
                history.replace(`${process.env.PUBLIC_URL}/new-order`);
                break;
            case 'Roz':
                history.replace(`${process.env.PUBLIC_URL}/settlements`);
                break;
            case 'Wsp':
                history.replace(`${process.env.PUBLIC_URL}/promotions`);
                break;
            case 'Isod':
                history.replace(`${process.env.PUBLIC_URL}/isod`);
                break;
            case 'Konto':
                history.replace(`${process.env.PUBLIC_URL}/account`);
                break;
            case 'Adm':
                history.replace(`${process.env.PUBLIC_URL}/admin`);
                break;
            case 'Kon':
                history.replace(`${process.env.PUBLIC_URL}/additional-tab`);
                break;
            case 'Ust':
                history.replace(`${process.env.PUBLIC_URL}/settings`);
                break;
            default:
                if (user?.role === USER_ROLE_SUPERVISIOR) history.replace(`${process.env.PUBLIC_URL}/orders`);
                else history.replace(`${process.env.PUBLIC_URL}/news`);
                break;
        }
    };

    const generateFilePreviewAndRedirect = async (fileName: string) => {
        window.history.replaceState({}, '', location.pathname);
        const url = await generateTempLink(fileName);
        const token = sessionStorage.getItem(USER_LOCAL_STORAGE);
        const parent = (window.opener as Window | null);
        if (token !== null && parent !== null) {
            parent.sessionStorage.setItem(USER_LOCAL_STORAGE, token);
            parent.location.reload();
            sessionStorage.clear();
        }

        if (url === '') return;
        window.open(buildResourceLink(url), '_self');
    };

    useEffect(() => {
        if (fileNameToGenerateForPreviewOnUrlExpired && user) {
            generateFilePreviewAndRedirect(fileNameToGenerateForPreviewOnUrlExpired);
        }
        if (userJustLoggedIn && !user?.forcePwdChange) {
            const redirectToDefault = (new URLSearchParams(window.location.search).get('redirectToDefault') ?? 'true') === 'true';
            if (redirectToDefault) {
                changeViewToDefault();
            }
        }
        store.dispatch(dataActions.setUserJustLoggedIn(false));
    }, [userJustLoggedIn]);

    useEffect(() => {
        if ((company !== null && user === null) || (user !== null && company !== null && company.toString() === user.company)) return;
        dispatch(setSliceState(window.location.hostname, user === null ? null : Number(user.company)));
    }, [user, dispatch, company]);

    const consoleLog = console.log;

    useEffect(() => {
        if (store.getState().conf.companyId !== null) {
            if (!store.getState().conf.isDebug) {
                console.log = () => { };
            } else {
                console.log = consoleLog;
            }
        }
    });

    useEffect(() => {
        if (!location.pathname.includes('new-order')) {
            if (edit) {
                store.dispatch(dataActions.setDetailedOrder({} as DetailedOrder));
                store.dispatch(dataActions.clearLocalCart());
                store.dispatch(dataActions.setEdit(false));
            }
            if (append) {
                store.dispatch(dataActions.setAppend(false));
            }
        }
        if (!location.pathname.includes('orders')) {
            store.dispatch(dataActions.setIsErrorGettingDetailedOrder(false));
        }
        setVisibility((location.pathname.includes('isod')) ? 'block' : 'none');
    }, [location.pathname]);

    useEffect(() => {
        if (showUsersDropdown) {
            setShowTermsDropdown(false);
        }
    }, [showUsersDropdown]);

    useEffect(() => {
        if (showTermsDropdown) {
            setShowUsersDropdown(false);
        }
    }, [showTermsDropdown]);

    useEffect(() => {
        if (!expanded) {
            setShowUsersDropdown(false);
            setShowTermsDropdown(false);
        }
    }, [expanded]);

    if (isConfigIncorrect) {
        return (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <h1 style={{ margin: '0', fontSize: '36px', lineHeight: '1.4' }}>
                    Wystąpił nieoczekiwany problem
                </h1>
                <span style={{ display: 'block', marginTop: '15px', fontSize: '16px' }}>
                    Skontaktuj się z administratorem
                </span>
            </div>
        );
    }

    if (location.pathname.includes('expired-url') && sessionService.getToken() === null) {
        return <UrlExpired />;
    }

    if (isUserFetching) {
        return invalidToken ? <Spinner showError errMsg={'Sesja wygasła, zaloguj się ponownie.'} button={{ label: 'Powrót do logowania', onClick: logoutClick } as SpinnerButtonType} /> : <Spinner centered />;
    }

    if ((user === null || user.forcePwdChange) && !tooManyAttempts) {
        if (cartReset === false) {
            setCartReset(true);
        }
        return (
            <>
                <RequestErrorModal />
                <Login />
            </>
        );
    }

    return (
        <div className='App'>
            <header className='App-header'>
                <RequestErrorModal />
                {!isDesktop && <NotificationComponent />}
                <div className='App-login-background'>
                    {user ?
                        <>
                            <div className='App-login-bar'>
                                <span className='align-items-center'>
                                    <span style={{ whiteSpace: 'nowrap' }}>Zalogowany jako:&nbsp;</span> <b style={{ whiteSpace: 'nowrap' }}>{user.username}</b>
                                </span>
                                {isDesktop ? (
                                    <span>
                                        Opiekun: {user.guardian},
                                        <a style={{ marginLeft: '4px' }} href={`mailto:${user.guardianMail}`}>{user.guardianMail}</a>,
                                        <a style={{ marginLeft: '4px' }} href={`tel:${user.guardianPhone}`}>{user.guardianPhone}</a>
                                    </span>
                                ) : (<></>)}
                                <span className='App-account-buttons align-items-center '>
                                    <Link to={`${process.env.PUBLIC_URL}/changePassword`}>
                                        <ButtonComponent
                                            whiteSpace='nowrap'
                                            marginBottom='0'
                                            marginTop='0'
                                            marginLeft='0'
                                            marginRight='0'
                                            text='Zmień hasło'
                                            onBlack
                                            height='32px'
                                            width='112px'
                                        />
                                    </Link>
                                    <ButtonComponent
                                        onClick={logoutClick}
                                        marginBottom='0'
                                        marginTop='0'
                                        marginLeft='0'
                                        marginRight='0'
                                        text='Wyloguj'
                                        onBlack
                                        height='32px'
                                    />
                                </span>
                            </div>
                        </> :
                        <div className='App-login-bar' style={{ height: '48px' }} />}
                </div>
            </header>
            {company !== null && (
                <div className='App-logo-bar' style={expanded ? { zIndex: 1002 } : undefined}>
                    <Navbar variant='dark' className={expanded && !isDesktop ? 'App-navbar-expanded' : 'App-navbar'} expand='lg' collapseOnSelect onToggle={() => setExpanded((val) => !val)} expanded={expanded}>
                        <Container className='App-navbar-container'>
                            {
                                user?.environment === 'dev' ? (
                                    <img style={{ maxHeight: !isDesktop ? '40px' : '60px' }} src={`${process.env.PUBLIC_URL}/${logo.replace('.png', '_test.png')}`} alt='Blachy Pruszyński' onClick={changeViewToDefault} />
                                ) : (
                                    <img style={{ maxHeight: !isDesktop ? '40px' : '60px' }} src={`${process.env.PUBLIC_URL}/${logo}`} alt='Blachy Pruszyński' onClick={changeViewToDefault} />
                                )
                            }
                            {!expanded && !isDesktop ? <div className='active-key-wrapper'><span className='active-key-name'>{activeKeyName}</span></div> : ''}
                            {!isDesktop &&
                                <div className='App-navbar-toggle' onClick={() => setExpanded((val) => !val)}>
                                    {!expanded && <div className='icon'>☰</div>}
                                    {expanded && <div className='icon x'>✖</div>}
                                    <div className='menu-text'>MENU</div>
                                </div>}
                            <Navbar.Collapse>
                                <Nav fill variant='pills' activeKey={activeKey} className='mr-auto'>
                                    <NavigationFactoryItems
                                        items={NAVBAR_TABS}
                                        context={{
                                            expanded,
                                            isDesktop,
                                            isPhone,
                                            activeKey,
                                            additionalTabName: additionalTabInfo.name,
                                            user,
                                            location,
                                            setShowUsersDropdown,
                                            showUsersDropdown,
                                            setShowTermsDropdown,
                                            showTermsDropdown
                                        }}
                                    />
                                </Nav>
                            </Navbar.Collapse>
                        </Container>
                    </Navbar>
                </div>
            )}
            <main className='App-body-container'>
                {company !== null && (
                    <div className='App-body'>
                        {
                            tooManyAttempts ? <Spinner showError errMsg={'Zbyt wiele żądań, spróbuj ponownie za minutę.'} /> :
                                invalidToken ? <Spinner showError errMsg={'Sesja wygasła, zaloguj się ponownie.'} button={{ label: 'Powrót do logowania', onClick: logoutClick } as SpinnerButtonType} /> : (
                                    <AppRoutes
                                        isDisabled={isDisabled}
                                        disableTime={disableTime}
                                        disableMessage={disableMessage}
                                        isPhone={isPhone}
                                        isISODVisible={isISODVisible}
                                        cartReset={cartReset}
                                        setCartReset={setCartReset}
                                        user={user}
                                    />
                                )
                        }
                    </div>
                )}
            </main>
            <footer className='App-footer'>
                <div className='App-footer-content'>
                    {user &&
                        <>
                            <Link to={`${process.env.PUBLIC_URL}/cookie-policy`}>
                                <Button variant='link' className='text-white font-weight-light'>
                                    Polityka cookies
                                </Button>
                            </Link>

                            <div style={{ display: 'flex' }}>
                                {user.environment === 'dev' ? <div className='text-white font-weight-light'>Webserwis testowy</div> : <></>}
                                {user.permissions.canAccessAdminPanel || permissionService.haveAccessToOperation('viewSystemVersion') ? <div className='version-number text-white'>{process.env.REACT_APP_VERSION}</div> : ''}
                            </div>
                        </>}
                </div>
            </footer>
        </div>
    );
};

export default App;
